const MATTER_STATUS_KEYS = {
  open: "clientsTable.matterStatuses.open",
  "re-opened": "clientsTable.matterStatuses.reopened",
  "pending termination": "clientsTable.matterStatuses.pendingTermination",
  terminated: "clientsTable.matterStatuses.terminated",
};

const ALERT_MESSAGE_KEYS = {
  "date of birth is missing": "alerts.missingDateOfBirth",
  "missing email address": "alerts.missingEmailAddress",
  "missing phone number": "alerts.missingPhoneNumber",
  "questionnaire not completed": "alerts.questionnaireNotCompleted",
  "retainer not signed": "alerts.retainerNotSigned",
};

export const MASTER_SCHEDULE_FILTERS = {
  ALL: "ALL",
  MS1: "MS1",
  MS2: "MS2",
  MS3: "MS3",
  INDIGENOUS: "INDIGENOUS",
  BUSINESS: "BUSINESS",
  NOT_ON_MASTER_SCHEDULE: "NOT_ON_MASTER_SCHEDULE",
};

export const MASTER_SCHEDULE_FILTER_VALUES = [
  MASTER_SCHEDULE_FILTERS.MS1,
  MASTER_SCHEDULE_FILTERS.MS2,
  MASTER_SCHEDULE_FILTERS.MS3,
  MASTER_SCHEDULE_FILTERS.INDIGENOUS,
  MASTER_SCHEDULE_FILTERS.BUSINESS,
  MASTER_SCHEDULE_FILTERS.NOT_ON_MASTER_SCHEDULE,
];

// Indigenous can never match on the Businesses tab. Businesses are no longer
// required to sit on a master schedule, so "Not on Master Schedule" applies.
export const BUSINESS_TAB_MASTER_SCHEDULE_FILTER_VALUES = [
  MASTER_SCHEDULE_FILTERS.MS1,
  MASTER_SCHEDULE_FILTERS.MS2,
  MASTER_SCHEDULE_FILTERS.MS3,
  MASTER_SCHEDULE_FILTERS.BUSINESS,
  MASTER_SCHEDULE_FILTERS.NOT_ON_MASTER_SCHEDULE,
];

// Once the Businesses tab exists it owns the business population, so the
// General tab drops the Business master schedule option.
export const GENERAL_WITH_BUSINESS_TAB_MASTER_SCHEDULE_FILTER_VALUES =
  MASTER_SCHEDULE_FILTER_VALUES.filter(
    (value) => value !== MASTER_SCHEDULE_FILTERS.BUSINESS,
  );

export const getMasterScheduleFilterValues = ({
  isBusinessTab = false,
  businessTabEnabled = false,
} = {}) => {
  if (isBusinessTab) return BUSINESS_TAB_MASTER_SCHEDULE_FILTER_VALUES;
  return businessTabEnabled
    ? GENERAL_WITH_BUSINESS_TAB_MASTER_SCHEDULE_FILTER_VALUES
    : MASTER_SCHEDULE_FILTER_VALUES;
};

export const sanitizeMasterScheduleFilter = (value, allowedValues = MASTER_SCHEDULE_FILTER_VALUES) =>
  allowedValues.includes(value) ? value : MASTER_SCHEDULE_FILTERS.ALL;

// Server-side client-type filter (`clientTypeFilter` query param); the list is
// paginated so there is no client-side counterpart.
export const CLIENT_TYPE_FILTERS = {
  ALL: "ALL",
  BUSINESS: "BUSINESS",
  INDIGENOUS: "INDIGENOUS",
};

export const CLIENT_TYPE_FILTER_VALUES = [
  CLIENT_TYPE_FILTERS.BUSINESS,
  CLIENT_TYPE_FILTERS.INDIGENOUS,
];

export const matchesMasterScheduleFilter = (
  masterSchedule,
  filter = MASTER_SCHEDULE_FILTERS.ALL,
) => {
  if (filter === MASTER_SCHEDULE_FILTERS.ALL) return true;

  const normalizedSchedule = String(masterSchedule || "").trim().toUpperCase();
  if (filter === MASTER_SCHEDULE_FILTERS.NOT_ON_MASTER_SCHEDULE) {
    return normalizedSchedule === "";
  }

  return normalizedSchedule === filter;
};

const CONTACT_QUALITY_VALUES = {
  "(green) no action required": {
    severity: "green",
    reasonKey: "noActionRequired",
  },
  "(amber) email address duplicate": {
    severity: "amber",
    reasonKey: "emailAddressDuplicate",
  },
  "(amber) email address required": {
    severity: "amber",
    reasonKey: "emailAddressRequired",
  },
  "(amber) email address requires correction": {
    severity: "amber",
    reasonKey: "emailAddressRequiresCorrection",
  },
  "(amber) phone number duplicate": {
    severity: "amber",
    reasonKey: "phoneNumberDuplicate",
  },
  "(amber) phone number required": {
    severity: "amber",
    reasonKey: "phoneNumberRequired",
  },
  "(amber) phone number requires correction": {
    severity: "amber",
    reasonKey: "phoneNumberRequiresCorrection",
  },
  "(red) email & phone require correction": {
    severity: "red",
    reasonKey: "emailAndPhoneRequireCorrection",
  },
  "(red) email & phone required": {
    severity: "red",
    reasonKey: "emailAndPhoneRequired",
  },
};

const SUSPICIOUS_DOB_PATTERN = /^suspicious date of birth\. client claims to be (\d+) years old\.?$/i;

const normalizeLookupKey = (value) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[.!?]+$/g, "")
    .replace(/\s+/g, " ");

export const translateMatterStatus = (status, t) => {
  if (!status) return "-";

  const key = MATTER_STATUS_KEYS[normalizeLookupKey(status)];
  return key ? t(key) : status;
};

export const translateAlertMessage = (message, t) => {
  if (!message) return "";

  const suspiciousDobMatch = String(message).trim().match(SUSPICIOUS_DOB_PATTERN);
  if (suspiciousDobMatch) {
    return t("alerts.suspiciousDateOfBirth").replace("{age}", suspiciousDobMatch[1]);
  }

  const key = ALERT_MESSAGE_KEYS[normalizeLookupKey(message)];
  return key ? t(key) : message;
};

export const parseContactQuality = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return {
      severity: "neutral",
      reasonKey: "statusUnavailable",
      rawReason: null,
      isKnown: false,
      isAvailable: false,
    };
  }

  const rawValue = String(value).trim();
  const knownValue = CONTACT_QUALITY_VALUES[normalizeLookupKey(rawValue)];
  if (knownValue) {
    return {
      ...knownValue,
      rawReason: null,
      isKnown: true,
      isAvailable: true,
    };
  }

  const prefixedValue = rawValue.match(/^\((green|amber|red)\)\s*(.*)$/i);
  return {
    severity: prefixedValue ? prefixedValue[1].toLowerCase() : "neutral",
    reasonKey: null,
    rawReason: prefixedValue?.[2] || rawValue,
    isKnown: false,
    isAvailable: true,
  };
};

export const getContactQualityDisplay = (value, t) => {
  const quality = parseContactQuality(value);
  return {
    ...quality,
    severityLabel: t(`clientsTable.contactQuality.severities.${quality.severity}`),
    reason: quality.reasonKey
      ? t(`clientsTable.contactQuality.reasons.${quality.reasonKey}`)
      : quality.rawReason,
  };
};
